import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AccommodationReservationForm } from "Bands/components/AccommodationReservationForm/AccommodationReservationForm";
import { dataMetaSelector } from "Bands/state";
import {
  dispatchGetEventAccommodationReservations,
  editEventAccommodationReservation,
} from "Bands/state/data/actions/eventBlocks/eventAccommodationReservation";
import { dispatchGetEventAccommodationRoomsForBlock } from "Bands/state/data/actions/eventBlocks/eventAccommodationRoom";
import {
  BAND_ACTIONS,
  IAccommodationReservation,
  IAccommodationSingleReservationFormParams,
  IAccommodationSingleReservationParams,
} from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { getIdFromPath } from "shared/utils";
import { dispatchGetEventAccommodationRoomTypes } from "Bands/state/data/actions/eventBlocks/eventAccommodationRoomType";

const { accommodationBlock: blockCopy } = copy;

interface EditAccommodationReservationProps {
  blockId: string;
  accommodationReservation: IAccommodationReservation;
  handleClose: () => void;
}

export const EditAccommodationReservationModal: FC<EditAccommodationReservationProps> =
  ({ blockId, accommodationReservation, handleClose }) => {
    const dispatch = useDispatch();

    const { api: apiMeta } = useSelector(
      dataMetaSelector(BAND_ACTIONS.editAccommodationReservation)
    );

    useClearActionMetaOnUnMount(BAND_ACTIONS.editAccommodationReservation);

    useEffect(() => {
      if (apiMeta?.status === IAPIState.succeeded) {
        dispatchGetEventAccommodationReservations({
          dispatch,
          blockId,
          page: 1,
        });

        handleClose();

        dispatch(
          addTemporaryToast({
            status: IStatus.success,
            message: blockCopy.create.peopleBlock.messages.edit,
          })
        );

        dispatchGetEventAccommodationRoomTypes(dispatch, blockId);
        dispatchGetEventAccommodationRoomsForBlock(dispatch, blockId);
      }
    }, [apiMeta?.status, blockId, dispatch, handleClose]);

    const handleSubmit = ({
      remark,
      room,
      roomType,
    }: IAccommodationSingleReservationFormParams) => {
      const cleanData: IAccommodationSingleReservationParams = {
        ...(roomType &&
          !room && {
            roomType: API_ENDPOINTS.EVENT_ACCOMMODATION_ROOM_TYPE(roomType),
          }),

        ...(room && {
          room: API_ENDPOINTS.EVENT_ACCOMMODATION_ROOM(room),
        }),

        ...(!room &&
          !roomType && {
            room: null,
          }),

        remark: remark,
      };

      dispatch(
        editEventAccommodationReservation({
          id: accommodationReservation.id,
          data: cleanData,
        })
      );
    };

    const buttons: IFormButtons = {
      primary: {
        children: "Save",
        "data-testid": "edit_accommodation_reservation_submit",
      },
      secondary: {
        children: "Cancel",
        onClick: handleClose,
      },
    };

    const defaultValues = useMemo(() => {
      const { bandUser, remark } = accommodationReservation;

      return {
        people: getIdFromPath(bandUser.id, API_ENDPOINTS.BAND_USERS),
        ...(accommodationReservation.roomType && {
          roomType: getIdFromPath(
            accommodationReservation.roomType.id,
            API_ENDPOINTS.EVENT_ACCOMMODATION_ROOM_TYPES
          ),
        }),
        ...(accommodationReservation.room && {
          room: getIdFromPath(
            accommodationReservation.room.id,
            API_ENDPOINTS.EVENT_ACCOMMODATION_ROOMS
          ),
        }),
        remark: remark,
      };
    }, [accommodationReservation]);

    return (
      <Modal
        title={blockCopy.create.peopleBlock.fields.editTitle}
        handleClose={handleClose}
        data-testid="edit_accommodation_reservation_modal"
      >
        <AccommodationReservationForm
          buttons={buttons}
          handleSubmit={handleSubmit}
          defaultValues={defaultValues}
          isSubmitting={apiMeta?.status === IAPIState.pending}
          apiError={apiMeta?.error}
          error={!!apiMeta?.error}
          blockId={blockId}
          isEdit={true}
          currentRoom={getIdFromPath(
            accommodationReservation.room?.id,
            API_ENDPOINTS.EVENT_ACCOMMODATION_ROOMS
          )}
        />
      </Modal>
    );
  };
